import { Link } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHero } from "@/components/shared/PageHero";
import { CTASection } from "@/components/shared/CTASection";
import { Calendar, Clock, ArrowRight, Loader2 } from "lucide-react";
import { format } from "date-fns";

// Import project images to use as blog images
import roofRestoration from "@/assets/projects/roof-restoration.jpg";
import sidingInstallation from "@/assets/projects/siding-installation-1.webp";
import sidingInstallation2 from "@/assets/projects/siding-installation-2.webp";
import residentialSiding from "@/assets/projects/residential-siding.webp";
import residentialRoofing from "@/assets/projects/residential-roofing.webp";
import residentialRoofing2 from "@/assets/projects/residential-roofing-2.jpg";
import roofSidingBarn from "@/assets/projects/roof-siding-barn.webp";
import sidingDeck from "@/assets/projects/siding-deck.jpg";

// Default placeholder image for posts without images
export const defaultBlogPostImage = roofRestoration;

export const staticBlogPosts = [
  {
    slug: "signs-need-new-roof",
    title: "5 Signs Your Roof Needs Replacement",
    excerpt:
      "Learn the telltale signs that indicate it's time for a new roof, from curling shingles to water stains on your ceiling.",
    created_at: "2026-01-08",
    read_time: "4 min read",
    category: "Roofing",
    image_url: roofRestoration,
  },
  {
    slug: "choosing-right-siding",
    title: "Vinyl vs. Fiber Cement Siding",
    excerpt:
      "Comparing siding options? We break down the pros and cons of vinyl versus James Hardie fiber cement siding for Minnesota homes.",
    created_at: "2026-01-05",
    read_time: "5 min read",
    category: "Siding",
    image_url: sidingInstallation,
  },
  {
    slug: "preparing-home-minnesota-winter",
    title: "Prep Your Home's Exterior for MN Winter",
    excerpt:
      "Before snow flies, prep your roof, gutters, and siding the right way. A Minnesota contractor's checklist for avoiding ice dams and costly winter damage.",
    created_at: "2026-01-02",
    read_time: "6 min read",
    category: "Maintenance",
    image_url: residentialRoofing,
  },
  {
    slug: "benefits-seamless-gutters",
    title: "Why Seamless Gutters Are Worth It",
    excerpt:
      "Seamless gutters offer better protection and less maintenance. Learn if upgrading is right for your home.",
    created_at: "2025-12-28",
    read_time: "5 min read",
    category: "Gutters",
    image_url: residentialSiding,
  },
  {
    slug: "ice-dam-prevention",
    title: "How to Prevent Ice Dams on Your Roof",
    excerpt:
      "Ice dams can cause serious damage to Minnesota homes. Learn prevention strategies and what to do if they form.",
    created_at: "2025-12-22",
    read_time: "4 min read",
    category: "Roofing",
    image_url: residentialRoofing2,
  },
  {
    slug: "deck-maintenance-tips",
    title: "Deck Maintenance for Minnesota Homeowners",
    excerpt:
      "Keep your deck looking great and lasting longer with these seasonal maintenance tips for our climate.",
    created_at: "2025-12-18",
    read_time: "7 min read",
    category: "Decks",
    image_url: sidingDeck,
  },
  {
    slug: "storm-damage-what-to-do",
    title: "What to Do After Roof Storm Damage",
    excerpt:
      "Steps to take after severe weather damages your roof, including insurance claims and temporary repairs.",
    created_at: "2025-12-12",
    read_time: "5 min read",
    category: "Roofing",
    image_url: roofSidingBarn,
  },
  {
    slug: "curb-appeal-siding-upgrade",
    title: "New Siding & Curb Appeal: The Payoff",
    excerpt:
      "See how a siding upgrade transforms a home's appearance, lifts resale value, and modernizes the exterior. Real before-and-afters from Minnesota projects.",
    created_at: "2025-12-08",
    read_time: "4 min read",
    category: "Siding",
    image_url: sidingInstallation2,
  },
];

const Blog = () => {
  const { data: dbPosts, isLoading } = useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("published", true)
        .or("scheduled_at.is.null,scheduled_at.lte.now()")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Combine database posts with static posts
  const allPosts = [...(dbPosts || []), ...staticBlogPosts];

  return (
    <>
      <main>
        <PageHero
          badge="Our Blog"
          title="Homeowner Tips & Guides"
          subtitle="Expert advice on maintaining and protecting your home's exterior."
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-secondary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {allPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group"
                  >
                    <article className="feature-card h-full flex flex-col hover:border-secondary transition-colors overflow-hidden p-0">
                      {/* Image */}
                      <div className="aspect-video overflow-hidden bg-muted">
                        <img
                          src={post.image_url || defaultBlogPostImage}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>

                      <div className="p-6 flex flex-col flex-1">
                        <div className="mb-4">
                          <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary/10 text-secondary">
                            {post.category}
                          </span>
                        </div>
                        <h2 className="text-xl font-heading font-bold text-foreground mb-3 group-hover:text-secondary transition-colors">
                          {post.title}
                        </h2>
                        <p className="text-muted-foreground mb-4 flex-1">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-sm text-muted-foreground pt-4 border-t border-border">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {format(
                                new Date(post.created_at),
                                "MMMM d, yyyy",
                              )}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {post.read_time}
                            </span>
                          </div>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <CTASection />
      </main>
    </>
  );
};

export default Blog;
